/**
 * The station computer's own database.
 *
 * Dexie rather than raw IndexedDB, which is the trade `device/store.ts` left
 * open ("the offline data layer that will want one is a later phase's decision
 * to make on its own evidence"). The evidence is this file: ten stores, one of
 * them holding a station's three-month history, and a schema that Phase 6 has to
 * be able to extend with an outbox and a conflict store without rewriting what
 * is already on disk. Versioned upgrades are the whole reason to take the
 * dependency, and hand-rolling them is how you get a device that loses a day's
 * work to a migration.
 *
 * Deliberately a *different* database from `eregister-device`. That one holds
 * the computer's identity — two non-extractable CryptoKeys it can never get back
 * if they are dropped. This one holds a cache that can always be refetched.
 * Clearing the cache must never be able to un-enrol the machine, and keeping
 * them apart is what makes that structurally true rather than a matter of care.
 *
 * From version 2 the second half of that sentence stops being true of one store.
 * `registerEntries` was a cache; it is now the *only* copy of work that has not
 * reached the server, and `mutations` is the record of what that work was. Both
 * are refetchable in the sense that the server will send back what it already
 * has — and in no sense at all for anything written since. Every migration from
 * here on has to preserve them or refuse to run (§22).
 *
 * That last sentence stopped being a convention in Phase 12. Every `.upgrade()`
 * below goes through `migrations/guard.ts`, which counts the work at risk on
 * either side of the callback and throws rather than commit a shortfall — and a
 * throw inside a version-change transaction is a database left exactly as it
 * was. `db/open.ts` is where the refusal becomes something an operator can read.
 */
import Dexie, { type Table } from "dexie"

import { preserving } from "./migrations/guard"

export const DATABASE_NAME = "eregister-offline"

/**
 * Reported to the server on every heartbeat, so an administrator can see a
 * device that is behind. Bump it in the same commit as a `.version()` block,
 * never on its own.
 */
export const SCHEMA_VERSION = 7

/** The signed snapshot, kept as the bytes that arrived. See `snapshot/verify.ts`. */
export interface StoredSnapshot {
  stationCode: string
  /** Part of the signed canonical string, so it is kept rather than re-derived. */
  stationUuid: string
  version: number
  /** The exact string the signature covers. Never re-serialised, never stored parsed. */
  payload: string
  digest: string
  signature: string
  publicKey: string
  issuedAt: string
  expiresAt: string
  verifiedAt: string
}

export interface StoredRegisterSchema {
  registerCode: string
  schemaVersion: number
  digest: string
  /** The whole `RegisterSchema::ClientBundle` payload, as fetched. */
  bundle: unknown
  fetchedAt: string
}

export interface StoredStationConfiguration {
  key: string
  value: unknown
}

export interface StoredUser {
  uuid: string
  name: string
  initials: string
  active: boolean
  roles: Array<{ uuid: string; title: string }>
}

export interface StoredReferenceData {
  key: string
  value: unknown
}

export interface StoredDutyState {
  id: "self"
  smInChargeUuid: string | null
  handingOverSmUuid: string | null
  takingOverSmUuid: string | null
  pendingHocEntryUuid: string | null
  /** "bootstrap" until Phase 6 derives it locally from the diary's own entries. */
  source: "bootstrap" | "derived"
  /**
   * The last duty-change mutation this machine queued, as the anchor the next
   * one depends on.
   *
   * `dependenciesFor` chains mutations within one entry, which is right for
   * edits to a caution order and not enough for a duty change: a hand over, its
   * take over and the next cycle's hand over are three different entries, and
   * nothing about their uuids says one follows another. Without an explicit
   * edge their order at the server is `device_seq`'s — correct in practice and
   * incidental, where §17.1 wants it proven.
   *
   * Null until this machine has made one.
   */
  lastDutyMutationUuid?: string | null
  updatedAt: string
}

export interface StoredSyncState {
  id: "self"
  cursor: number | null
  lastBootstrapAt: string | null
  /** Per-step progress, so a transfer interrupted halfway resumes rather than restarts. */
  bootstrapProgress: Record<string, BootstrapStepRecord>
  windowStart: string | null
  /** Both null until Phase 7 drains anything. "Last synced" in §19.2 reads the push. */
  lastPushAt: string | null
  lastPullAt: string | null
  protocolVersion: string | null
  /**
   * Where the notification feed had got to (§16.4). An `updated_at` rather than
   * a journal position, because notifications have no change journal — see
   * `Sync::NotificationFeed`.
   */
  notificationCursor?: string | null
  /**
   * When the server last refused this computer's protocol version (§22).
   *
   * Durable rather than held in the scheduler, because the refusal outlives the
   * page that provoked it and the remedy — a new build — arrives on somebody
   * else's shift. Null once a cycle completes again.
   */
  upgradeRequiredAt?: string | null
  upgradeRequiredDetail?: UpgradeRequirementRecord | null
  /**
   * When the server last said the delta feed can no longer carry this device
   * (§22). Durable because a re-bootstrap is supervised: the pull that learned
   * it and the operator who acts on it are days and a shift change apart.
   */
  rebootstrapRequiredAt?: string | null
  /** `Sync::RebootstrapPolicy#reason` — which of the three it is. */
  rebootstrapReason?: string | null
  /**
   * When the retention sweep last ran (§14). Not a schedule — the sweep is
   * driven by successful syncs — but the evidence that it is running at all,
   * which on a machine that never reaches the server is the interesting fact.
   */
  lastRetentionSweepAt?: string | null
}

/** What `Sync::ProtocolVersion.refusal_details` sent, kept for the indicator. */
export interface UpgradeRequirementRecord {
  /** What the server does support, so the readiness page can name the gap. */
  supportedVersions: string[]
  currentVersion: string | null
  requestedVersion: string | null
  /** `superseded` — this computer is behind — or `unknown`/`missing`. */
  status: string | null
  /**
   * The protocol version *this* client was speaking when it was refused.
   *
   * The load-bearing field, and the reason the refusal is not simply a flag. A
   * flag that stops every cycle can never be cleared by a cycle, and the remedy
   * — a new build — arrives without anybody's hand on a reset. Recorded against
   * the version, the refusal stops applying the moment the page is speaking a
   * different one, and the first successful cycle after that clears it.
   */
  refusedVersion: string | null
}

export interface BootstrapStepRecord {
  status: "pending" | "running" | "done" | "failed"
  error?: string
  /** Keyset cursor within a paginated step. */
  cursor?: number | null
  entryCount?: number
  attempts: number
  updatedAt: string
}

export interface StoredDeviceState {
  id: "self"
  /**
   * The monotonic time floor. See `clock.ts` — this is what stops a wound-back
   * OS clock from reviving an expired snapshot.
   */
  timeFloorMs: number | null
  timeFloorCapturedAtWallMs: number | null
  clockOffsetMs: number | null
  clockOffsetMeasuredAt: string | null
  clockRegressionDetectedAt: string | null
  /** Codes already accepted, so one cannot be typed in twice. */
  replay: Array<{ fingerprint: string; acceptedAt: string; expiresAt: string }>
  /** Timestamps of recent failures, for the local escalating backoff. */
  failures: number[]
  localSession: LocalSession | null
  /** Whether the two CryptoKeys really are non-extractable on this browser. */
  keyExtractability: { signing: boolean; offlineSecret: boolean } | null
  storagePersisted: boolean | null
  publicKey: string | null
  /**
   * The challenge currently on the offline sign-in screen. Stored rather than
   * held in the page, because a code the operator is already holding was minted
   * against one particular nonce: if a reload — or a crash, or the page being
   * reopened from the cache — mints a new one, that code is refused, and the
   * refusal names none of this.
   */
  activeChallenge: { nonce: string; expiresAtMs: number } | null
}

export interface LocalSession {
  userUuid: string
  actingRoleUuid: string | null
  stationCode: string
  deviceUuid: string
  mode: "read" | "write"
  authenticatedAt: string
  expiresAt: string
  authorizationSnapshotVersion: number
}

/**
 * §10's `audit_log`: offline authentication recorded locally and queued for
 * sync (§7.5). The only store here written by the device rather than filled from
 * the server, and not the outbox — the outbox carries register mutations and is
 * Phase 6's to build.
 */
export interface StoredAuditRecord {
  uuid: string
  kind: "offline_login" | "offline_login_refused" | "offline_logout"
  userUuid: string | null
  stationCode: string
  deviceUuid: string
  mode: "read" | "write" | null
  outcome: string
  authorizationSnapshotVersion: number | null
  occurredAt: string
  syncedAt: string | null
}

/**
 * A register entry as this machine holds it.
 *
 * Version 1 filled this from the server and read it back nowhere. From version 2
 * it is what the register renders, which means it is also what a local write
 * changes — and the columns below the `entry` blob exist because IndexedDB can
 * only index what is at the top level of a record. Sorting a register by date,
 * counting what has not been sent, and excluding deleted rows all happen on
 * every keystroke of a search; none of them can afford to deserialise the blob.
 *
 * `deleted` and `unsynced` are `0 | 1` rather than booleans because a boolean is
 * not a valid IndexedDB key. Dexie will accept the property and silently decline
 * to index it, which reads as a working index right up until the table is large
 * enough for the difference to matter.
 */
export interface StoredRegisterEntry {
  uuid: string
  registerCode: string
  occurredAt: string
  seqNo: string | null
  /** The serialised entry, in the shape `RegisterEntrySerializer` produces. */
  entry: unknown
  /** The revision this record is at: the server's, or the local one a write bumped it to. */
  revision: number
  deleted: 0 | 1
  /** 1 while any mutation touching this entry is still in the outbox. */
  unsynced: 0 | 1
  /**
   * 1 once the server has refused a mutation touching this entry, or one has
   * exhausted its attempts.
   *
   * Its own column rather than a state derived from `unsynced`, and for the same
   * reason `rejectedCount` is excluded from `unsyncedCount`: a refusal is
   * terminal. Folding it into "not yet synchronized" would tell an operator to
   * keep waiting for something that will never arrive.
   */
  rejected: 0 | 1
  /**
   * 1 while any mutation touching this entry is waiting for somebody to decide.
   *
   * Its own indexed column for the same reason `rejected` is: a conflicted entry
   * is neither synchronised nor on its way, and folding it into either would
   * tell an operator to wait for something that is waiting for them.
   */
  conflicted: 0 | 1
  /** `OFF/<short-code>/<device-seq>`, and null once the server assigns a real one. */
  provisionalSeqNo: string | null
  /**
   * The local projection of `RegisterEntry#searchable_text`, stored verbatim as
   * the column is — the server searches it with ILIKE, so the case-folding
   * belongs in the comparison rather than in the stored text.
   *
   * Null means "not projected yet" rather than "nothing to match", which is a
   * distinction the empty string cannot carry: every entry's text includes its
   * `seq_no`, so a genuinely projected row is never empty. Rows arrive null from
   * the server and from the version-1 upgrade — computing the text needs the
   * register's schema to know which fields are searchable, and neither a
   * serializer nor a Dexie upgrade transaction has one.
   */
  searchable: string | null
  /**
   * The entry this one closes, indexed because a hand over sheet asks the
   * question in bulk.
   *
   * `unclosedEntriesFor` — the basis of the S&T maintenance, S&T disconnection
   * and road position sections — needs the set of entries nothing references.
   * Answering that from inside the `entry` blob would mean deserialising every
   * diary entry the station holds on every render of the hand over form.
   *
   * Null for every register but the station diary, and null there for anything
   * that closes nothing.
   */
  originalEntryUuid: string | null
  /**
   * For a broadcast reference: the source entry it points at.
   *
   * Its own indexed column rather than a read into `data`, for the same reason
   * as above and one more — every write to a broadcast-source register has to
   * ask "is there already a reference for this entry", and Dexie cannot index
   * into nested JSON. Without it that question is a full scan of the diary on
   * every caution order, power block and line block write of a 14-day outage.
   */
  broadcastSourceEntryUuid: string | null
  updatedAt: string
}

/**
 * The last state the server confirmed, kept per entry for the three-way merge
 * Phase 8 performs (§13.1).
 *
 * Written by Phase 6 because it can only be captured at the moment a local write
 * overwrites it — by the time the merge needs it, the original is gone. A store
 * that starts being filled one phase after it starts being needed is a store
 * whose oldest entries are the ones it has nothing for.
 */
export interface StoredEntryBaseSnapshot {
  entryUuid: string
  revision: number
  data: Record<string, unknown>
  capturedAt: string
}

/** §11's operations. `resolve_conflict` is Phase 8's; the rest are Phase 6's. */
export type MutationOperation = "create" | "update" | "soft_delete" | "restore" | "resolve_conflict"

export type MutationState = "pending" | "syncing" | "acknowledged" | "conflict" | "rejected" | "superseded"

/**
 * One durable mutation: §11, field for field.
 *
 * `changedPaths` and `values` rather than the whole document, and that is the
 * decision the whole conflict engine rests on. A mutation carrying the entry as
 * it now stands would make every concurrent edit to any field a conflict with
 * every other, because there would be no way to tell what this writer actually
 * touched from what they merely re-sent.
 */
export interface StoredMutation {
  mutationUuid: string
  entryUuid: string
  /** Null in a browser that is not an enrolled station computer. */
  stationDeviceUuid: string | null
  actingUserUuid: string
  actingRoleUuid: string | null
  stationUuid: string
  stationCode: string
  registerCode: string
  operation: MutationOperation
  changedPaths: string[]
  /** The server revision this was made against; 0 for an entry created offline. */
  baseRevision: number
  /** New values at the changed paths only, keyed by path. */
  values: Record<string, unknown>
  /**
   * What this machine last agreed with the server those paths were, for the
   * Base column of a conflict (§13.1) — and for nothing else. The server never
   * reads it to decide *whether* there is a conflict, because it comes from one
   * of the two parties to the disagreement; it reads its own change journal for
   * that. See `Sync::FieldMerger`.
   */
  baseValues: Record<string, unknown>
  /**
   * `{"caution_details.1": "<uid>"}` — which row each index this mutation
   * touched actually refers to.
   *
   * Only this machine can say. A row inserted above on the server renumbers
   * everything below it, and the server cannot rebuild the document this was
   * written against to work out which row index 1 was. Without this, an edit to
   * one row lands on another the moment anybody reorders anything.
   */
  rowUids: Record<string, string>
  deviceSeq: number
  occurredAt: string
  authorizationSnapshotVersion: number | null
  schemaVersion: number | null
  /**
   * The register definition this was written against, fingerprinted.
   * `schemaVersion` is authored by hand and does not move when a definition is
   * edited in place, so it cannot answer whether the two sides agree (§22).
   */
  schemaDigest: string | null
  /** Mutation uuids the server must accept before this one (§12.1). */
  dependsOn: string[]
  /** The conflict this mutation settles, for `resolve_conflict` and nothing else. */
  conflictUuid: string | null
  /** Which of a rule conflict's permitted answers was chosen. */
  domainResolution: string | null
  resolutionNote: string | null
  /**
   * Which section of a section-partitioned register a create was written on.
   *
   * Null for every other register and every other operation. The server cannot
   * derive it — the page that chose the section is gone by the time an outbox
   * drains — and for `lc_pn` it is what decides whether the applied entry has
   * any crossing fields at all.
   */
  lcSectionId: string | null
  state: MutationState
  attemptCount: number
  lastError: string | null
  createdAt: string
}

/** A value conflict is a choice per path; a rule conflict is not (§13.2). */
export type ConflictKind = "field" | "domain"

/** One row of §13.1's Field / Base / Offline / Server table. */
export interface ConflictField {
  path: string
  base: unknown
  offline: unknown
  server: unknown
  /**
   * The paragraphs both writers rewrote, for a field the merge could cut up
   * (§30.1.4). Empty for every field type but rich text, which is why nothing
   * that reads the three columns above has to know about it.
   */
  regions?: ConflictRegion[]
  /**
   * The document with the offline side standing in at every region. What a
   * per-paragraph choice is spliced into, so the client rebuilds the answer
   * without re-running the merge — which is the server's to run.
   */
  draft?: string | null
}

/** One paragraph the two writers disagreed about, and where it sits in `draft`. */
export interface ConflictRegion {
  index: number
  length: number
  base: string
  offline: string
  server: string
}

/** What the resolver chose for one path. */
export type FieldChoice = "offline" | "server" | "resolved"

export interface ConflictResolution {
  /** Per path for a field conflict; absent for a rule conflict. */
  fields: Record<string, { choice: FieldChoice; value: unknown }>
  /** One of the invariant's own permitted answers, for a rule conflict. */
  domainChoice: string | null
  note: string | null
  resolverUuid: string
  resolvedAt: string
  /** The mutation the decision became, so the outbox and the row agree. */
  mutationUuid: string
}

/**
 * A conflict awaiting a human (§13).
 *
 * Phase 7 wrote a stub of this — the mutation that conflicted and the error
 * code, which is enough to count and nothing like enough to decide. What Phase 8
 * adds is the three values, because they cannot be recovered later: the server's
 * attempted write rolled back, and this machine holds its own value and the one
 * it started from but by definition not the one it disagrees with.
 *
 * The whole payload is served by the server rather than reassembled here, for
 * the same reason the message is: a conflict raised against *another* computer's
 * mutation has to be resolvable from this one, and this one holds none of it.
 */
export interface StoredConflict {
  uuid: string
  entryUuid: string
  registerCode: string
  mutationUuid: string
  /** The invariant's code, or `field_conflict`. */
  kind: ConflictKind
  code: string
  /** In business language, composed server-side from records this machine may not hold. */
  message: string
  baseRevision: number | null
  /**
   * What the server was at when it refused. A resolution is written against
   * this and not against the local revision counter, which has climbed since.
   */
  serverRevision: number | null
  fields: ConflictField[]
  /** Whatever the invariant said about itself: the entries it collided with, and so on. */
  details: Record<string, unknown>
  /** Declared by the registry, never inferred from the code — see the serializer. */
  permittedResolutions: string[]
  /** Who made the change, which during an outage is routinely not who decides. */
  actingUserName: string | null
  seqNo: string | null
  detectedAt: string
  /** Indexed, so the indicator's count is a query rather than a scan. See `deleted`. */
  resolved: 0 | 1
  resolvedAt: string | null
  resolution: ConflictResolution | null
}

/**
 * What a locally created entry is called until the server names it.
 *
 * Kept in its own store rather than only inside the entry, so that the
 * allocation survives the entry being rewritten and so two entries can never be
 * given the same one — the row is the allocation, and `deviceSeq` is unique by
 * construction.
 */
export interface StoredProvisionalSequence {
  entryUuid: string
  registerCode: string
  deviceSeq: number
  /** `OFF/<short-code>/<device-seq>`, stored in `data["provisional_seq_no"]` too. */
  value: string
  allocatedAt: string
}

/**
 * The outbox's own counter, and the identity that makes its numbers unique.
 *
 * `deviceSeq` is allocated inside the same transaction as the mutation that uses
 * it, which is what makes it monotonic across tabs: IndexedDB serialises
 * read-write transactions over the same store, so two tabs cannot both read 4.
 */
export interface StoredOutboxState {
  id: "self"
  deviceSeq: number
  /** From the enrolled device's credential, or minted here when there is none. */
  shortCode: string | null
  /** Identifies this browser context when no device is enrolled. */
  contextUuid: string | null
}

/**
 * A notification this machine raised for itself, while it had no server to raise
 * it on (§17.3).
 *
 * There is exactly one of these today: the notice a take over leaves behind
 * listing what was handed over unresolved. It is written in the same transaction
 * as the take over, under the **same supersede key the server uses**, and it is
 * retired the moment the take over's mutation is accepted — because that is when
 * the server writes the real one, and two notices saying the same thing is worse
 * than either.
 *
 * Not a general offline notifications store. Cached server notifications, local
 * read and dismiss, and the panel's last-synchronised line are Phase 10's, and
 * they will read this table alongside the cached ones rather than replacing it:
 * a notice about an outage is raised during the outage or not at all.
 */
export interface StoredLocalNotification {
  uuid: string
  /** `Notification#supersede_key`, matching the server's for the same event. */
  supersedeKey: string
  title: string
  message: string
  priority: boolean
  createdAt: string
  /**
   * The mutation whose acceptance retires this notice. Null for a notice with
   * nothing to wait for, which is then permanent until superseded locally.
   */
  awaitingMutationUuid: string | null
}

/**
 * A notification as the server sent it (§16.4).
 *
 * The recipient is kept as a type and a uuid rather than as "visible to me",
 * because visibility is not a property of the notification: a station-addressed
 * one belongs to whoever is currently the SM in charge, and during an outage
 * that changes on this machine, at a take over nobody can ask the server about.
 * `visibleTo` re-derives the rule from local duty state, which is the whole
 * point of caching the fact rather than the answer.
 */
export interface StoredNotification {
  uuid: string
  title: string
  message: string | null
  notificationType: "info" | "warning" | "success" | "error"
  priority: boolean
  /** 0/1 rather than a boolean: Dexie cannot index one. See `StoredConflict#resolved`. */
  read: 0 | 1
  dismissedAt: string | null
  supersedeKey: string | null
  recipientType: "User" | "Station"
  recipientUuid: string | null
  /** The station's code, for the badge beside a station-addressed notice. */
  recipientCode: string | null
  /** `{ kind, label, href }`, flattened server-side; null when there is nothing to do. */
  action: { kind: string; label: string; href: string } | null
  createdAt: string
  updatedAt: string
}

/** Read or dismiss, taken locally and owed to the server. */
export interface StoredNotificationAction {
  uuid: string
  notificationUuid: string
  action: "read" | "dismiss"
  actingUserUuid: string
  /** When the operator did it, which is not when the server hears about it. */
  at: string
  state: MutationState
  attemptCount: number
  lastError: string | null
}

export class OfflineDatabase extends Dexie {
  snapshot!: Table<StoredSnapshot, string>
  registerSchemas!: Table<StoredRegisterSchema, string>
  stationConfiguration!: Table<StoredStationConfiguration, string>
  users!: Table<StoredUser, string>
  referenceData!: Table<StoredReferenceData, string>
  dutyState!: Table<StoredDutyState, string>
  syncState!: Table<StoredSyncState, string>
  deviceState!: Table<StoredDeviceState, string>
  registerEntries!: Table<StoredRegisterEntry, string>
  auditLog!: Table<StoredAuditRecord, string>
  mutations!: Table<StoredMutation, string>
  entryBaseSnapshots!: Table<StoredEntryBaseSnapshot, string>
  conflicts!: Table<StoredConflict, string>
  provisionalSequences!: Table<StoredProvisionalSequence, string>
  outboxState!: Table<StoredOutboxState, string>
  localNotifications!: Table<StoredLocalNotification, string>
  notifications!: Table<StoredNotification, string>
  notificationActions!: Table<StoredNotificationAction, string>

  constructor(name: string = DATABASE_NAME) {
    super(name)

    this.version(1).stores({
      snapshot: "stationCode",
      registerSchemas: "registerCode",
      stationConfiguration: "key",
      users: "uuid, active",
      referenceData: "key",
      dutyState: "id",
      syncState: "id",
      deviceState: "id",
      registerEntries: "uuid, registerCode, occurredAt, [registerCode+occurredAt]",
      // Indexed on syncedAt so the drain in a later phase can find what has not
      // been sent without reading every row.
      auditLog: "uuid, syncedAt, occurredAt"
    })

    // Version 1 is kept above rather than folded into this one. Dexie replays
    // every version in order to open a database at an older one, so deleting the
    // first block would make a device that has been offline since Phase 5 open
    // an empty database rather than an upgraded one.
    this.version(2)
      .stores({
        registerEntries:
          "uuid, registerCode, occurredAt, unsynced, [registerCode+occurredAt], [registerCode+deleted]",
        mutations: "mutationUuid, entryUuid, state, deviceSeq, [state+deviceSeq]",
        entryBaseSnapshots: "entryUuid",
        conflicts: "uuid, entryUuid, resolved",
        provisionalSequences: "entryUuid, deviceSeq",
        outboxState: "id"
      })
      .upgrade(preserving(2, async transaction => {
        // Version 1's rows carry none of the columns version 2 indexes, and an
        // absent property is not indexed at all — a register bootstrapped under
        // Phase 5 would come back with an empty grid. Backfilling is the whole
        // job of this upgrade.
        await transaction
          .table<StoredRegisterEntry>("registerEntries")
          .toCollection()
          .modify(record => {
            const entry = (record.entry ?? {}) as Record<string, unknown>

            record.revision = numberOr(entry.revision, 0)
            record.deleted = entry.deleted === true ? 1 : 0
            record.unsynced = 0
            record.provisionalSeqNo = null
            record.searchable = null
            record.updatedAt = new Date().toISOString()
          })

        await transaction
          .table<StoredSyncState>("syncState")
          .toCollection()
          .modify(record => {
            record.lastPushAt ??= null
            record.lastPullAt ??= null
            record.protocolVersion ??= null
          })
      }))

    // Phase 7. One indexed column, because for the first time the server can
    // answer "no" — and a refusal an operator cannot see is a refusal that
    // silently loses the change it refused.
    this.version(3)
      .stores({
        registerEntries:
          "uuid, registerCode, occurredAt, unsynced, rejected, [registerCode+occurredAt], [registerCode+deleted]"
      })
      .upgrade(preserving(3, async transaction => {
        // The same backfill version 2 needed and for the same reason: an absent
        // property is not indexed at all, so without this every row written
        // before this version would be invisible to a query on `rejected` —
        // which reads as "nothing was refused" rather than as "nothing is known".
        await transaction
          .table<StoredRegisterEntry>("registerEntries")
          .toCollection()
          .modify(record => {
            record.rejected = 0
          })
      }))

    // Phase 8. The conflict store stops being a placeholder and starts holding
    // what a person needs to decide.
    this.version(4)
      .stores({
        registerEntries:
          "uuid, registerCode, occurredAt, unsynced, rejected, conflicted, " +
          "[registerCode+occurredAt], [registerCode+deleted]",
        conflicts: "uuid, entryUuid, mutationUuid, resolved, [registerCode+resolved]"
      })
      .upgrade(preserving(4, async transaction => {
        await transaction
          .table<StoredRegisterEntry>("registerEntries")
          .toCollection()
          .modify(record => {
            record.conflicted = 0
          })

        // Mutations queued before this version carry neither. Empty rather than
        // absent, so a push does not send `undefined` for a field the server now
        // reads — and so the property exists to be written next time.
        await transaction
          .table<StoredMutation>("mutations")
          .toCollection()
          .modify(record => {
            record.baseValues ??= {}
            record.rowUids ??= {}
            record.schemaDigest ??= null
            record.conflictUuid ??= null
            record.domainResolution ??= null
            record.resolutionNote ??= null
          })

        // Phase 7's conflict rows named a mutation and an error code and nothing
        // else, and there is no honest way to invent the rest: the three values
        // were never on this machine. They are backfilled to an empty field list
        // rather than to a plausible-looking one, and the Conflict Centre says
        // so — "recorded before this version, and its detail was not kept" is a
        // true statement, where an empty table would read as "nothing differs".
        await transaction
          .table<StoredConflict>("conflicts")
          .toCollection()
          .modify(record => {
            record.kind = "field"
            record.code ??= "conflict"
            record.message ??= "This change conflicted with the server before conflict detail was recorded."
            record.baseRevision ??= null
            record.serverRevision ??= null
            record.fields ??= []
            record.details ??= {}
            record.permittedResolutions ??= []
            record.actingUserName ??= null
            record.seqNo ??= null
            record.resolution ??= null
          })
      }))

    // Phase 9. Two references a hand over sheet has to be able to follow in
    // bulk, lifted out of the blob so they can be indexed.
    this.version(5)
      .stores({
        registerEntries:
          "uuid, registerCode, occurredAt, unsynced, rejected, conflicted, originalEntryUuid, " +
          "[registerCode+occurredAt], [registerCode+deleted], [registerCode+broadcastSourceEntryUuid]"
      })
      .upgrade(preserving(5, async transaction => {
        await transaction
          .table<StoredRegisterEntry>("registerEntries")
          .toCollection()
          .modify(record => {
            const entry = (record.entry ?? {}) as Record<string, unknown>
            const data = (entry.data ?? {}) as Record<string, unknown>

            // Derived from the blob rather than left null, because these rows
            // predate the column and the sections that read it would otherwise
            // treat every closed event on this device as still open — which
            // reads as work outstanding that was finished days ago, on the one
            // document where that matters most.
            //
            // The server sends `originalEntryUuid` from its own column from
            // this version on; this is only for what is already on disk.
            record.originalEntryUuid =
              (entry.originalEntryUuid as string | undefined) ?? closesUuidFrom(data) ?? null
            record.broadcastSourceEntryUuid = (data.source_entry_uuid as string | undefined) ?? null
          })
      }))

    // Phase 9, §17.3. Additive — a new store needs no upgrade, because every
    // existing row is in a store this does not touch.
    this.version(6).stores({
      localNotifications: "uuid, supersedeKey, awaitingMutationUuid"
    })

    // Phase 10, §16.4. Additive, and beside `localNotifications` rather than
    // replacing it: one store holds what the server sent and the other holds
    // what this machine raised for itself during an outage, and the panel reads
    // both. `notificationActions` is the second durable queue in this database
    // — see `Sync::NotificationActions` for why it is not the outbox.
    this.version(7).stores({
      notifications: "uuid, recipientType, read, dismissedAt, supersedeKey, updatedAt",
      notificationActions: "uuid, notificationUuid, state"
    })
  }
}

/**
 * Which entry a station-diary entry closes, out of its own `data`.
 *
 * `data["corresponding_event"][data["event"]]` is what the server's own
 * `assign_original_entry_from_corresponding_event_field` reads before it
 * resolves the column, so a row written before the column existed still carries
 * the answer — it was never anywhere else.
 */
function closesUuidFrom(data: Record<string, unknown>): string | null {
  const event = data.event
  const corresponding = data.corresponding_event
  if (typeof event !== "string" || corresponding === null || typeof corresponding !== "object") return null

  const uuid = (corresponding as Record<string, unknown>)[event]

  return typeof uuid === "string" && uuid !== "" ? uuid : null
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback
}

let database: OfflineDatabase | null = null

/**
 * One connection per page. Dexie handles the open lifecycle, so unlike
 * `device/store.ts` there is nothing to close after each read.
 */
export function offlineDatabase(): OfflineDatabase {
  database ??= new OfflineDatabase()
  return database
}

/** Test seam. Not for application code — a second connection means a second cache. */
export function useOfflineDatabase(instance: OfflineDatabase | null): void {
  database = instance
}

export const DEVICE_STATE_KEY = "self"

export const EMPTY_DEVICE_STATE: StoredDeviceState = {
  id: DEVICE_STATE_KEY,
  timeFloorMs: null,
  timeFloorCapturedAtWallMs: null,
  clockOffsetMs: null,
  clockOffsetMeasuredAt: null,
  clockRegressionDetectedAt: null,
  replay: [],
  failures: [],
  localSession: null,
  keyExtractability: null,
  storagePersisted: null,
  publicKey: null,
  activeChallenge: null
}

export async function readDeviceState(db: OfflineDatabase = offlineDatabase()): Promise<StoredDeviceState> {
  return (await db.deviceState.get(DEVICE_STATE_KEY)) ?? EMPTY_DEVICE_STATE
}

export async function updateDeviceState(
  changes: Partial<StoredDeviceState>,
  db: OfflineDatabase = offlineDatabase()
): Promise<StoredDeviceState> {
  return db.transaction("rw", db.deviceState, async () => {
    const current = (await db.deviceState.get(DEVICE_STATE_KEY)) ?? EMPTY_DEVICE_STATE
    const next = { ...current, ...changes, id: DEVICE_STATE_KEY } as StoredDeviceState
    await db.deviceState.put(next)
    return next
  })
}

export const SYNC_STATE_KEY = "self" as const

export const EMPTY_SYNC_STATE: StoredSyncState = {
  id: SYNC_STATE_KEY,
  cursor: null,
  lastBootstrapAt: null,
  bootstrapProgress: {},
  windowStart: null,
  lastPushAt: null,
  lastPullAt: null,
  protocolVersion: null
}

export async function readSyncState(db: OfflineDatabase = offlineDatabase()): Promise<StoredSyncState> {
  return (await db.syncState.get(SYNC_STATE_KEY)) ?? EMPTY_SYNC_STATE
}

export async function updateSyncState(
  changes: Partial<StoredSyncState>,
  db: OfflineDatabase = offlineDatabase()
): Promise<StoredSyncState> {
  return db.transaction("rw", db.syncState, async () => {
    const current = (await db.syncState.get(SYNC_STATE_KEY)) ?? EMPTY_SYNC_STATE
    const next = { ...current, ...changes, id: SYNC_STATE_KEY }
    await db.syncState.put(next)
    return next
  })
}
